import { Component, Element, h, Host, Listen, Prop, State } from '@stencil/core';
import { fetchWithTimeout } from '@ticom/web-component-utils';
import { FeatureDialogService } from '../feature-dialog-launcher/feature-dialog-launcher.service';
import productStrings from '../../global/l10n/ProductEntryPoint.json';
import softwareStrings from '../../global/l10n/SoftwareEntryPoint.json';

@Component({
    tag: 'ti-product-notifications',
    styleUrl: 'product-notifications.scss'
})
export class AddProductNotification {

    /**
     * private class variables
     */

    private _dialogTitle: string;

    private _externalProductEntryPointAPIUrl: string;

    private _isDialogOpen: boolean = false;

    private readonly _timeout: number = 5000;

    private _localeStrings;

    /**
     * host element
     */
    @Element() hostElement: HTMLElement;

    /**
     * Locale.
     *
     * @type {string} Possible values include 'en-US','zh-CN','ja-JP'.
     * @default 'en-US'
     */
    @Prop() locale: string = 'en-US';

    /**
     * part number
     *
     * @type {string}
     */
    @Prop() partNumber: string;

    /**
     * Determines if notifcation should be product or software based, options are 'product' or 'software', defaults to 'product'
     *
     * @type {string}
     * @memberof AddProductNotification
     */
    @Prop({ mutable: true }) type: string = 'product';

    /**
     * Determines if component should be hidden
     *
     * @type {boolean}
     * @memberof AddProductNotification
     */
    @State() hide: boolean = true;

    /**
     * Lifecycle event handler
     */
    componentWillLoad() {
        this.type = this.type.toLowerCase();
        if('software' === this.type) {
            this._localeStrings = softwareStrings;
        } else {
            this._localeStrings = productStrings;
        }

        this._externalProductEntryPointAPIUrl = `/notification-settings/api/p/productinfo/${this.partNumber}`;
        fetchWithTimeout(this._externalProductEntryPointAPIUrl, {
            method: 'GET',
            credentials: 'include'
        }, this._timeout).then((response: any) => {
            if (response.ok) {
                return response.json();
            } else {
                return Promise.reject(new Error(`Failed to retrieve ${this.type} alert status`));
            }
        }).then((json: {
            validAndEligiblePart: boolean;
        }) => {
            if (json.validAndEligiblePart) {
                this.hide = false;
            }
        }).catch((err) => {
            console.error(err);
        });
    }


    @Listen('tiDialogClosed', { target: 'window' })
    handleTiDialogClosed() {
        this._isDialogOpen = false;
    }

    /**
     * Closes the dialog
     */
    private _closeDialog() {
        FeatureDialogService.closeDialog();
        this._handleMetrics(`add ${this.type} notification`, 'cancel', '');
    }

    /**
     * Fires metric event
     *
     * @param action - action taken
     * @param label  - label of object action is taken on
     * @param link   - populated if action navigates to new page
     */
    private _handleMetrics(action, label, link) {
        if (typeof (window as any)._tiAnalyticsTrack !== 'undefined') {
            (window as any)._tiAnalyticsTrack('custom event',
                {
                    'event-name': `${this.type}_notifications_modal`,
                    'legacy-event-category': `${this.type} notification modal`,
                    'legacy-event-action': action,
                    'legacy-event-label': label,
                    'data-product': 'alert-management',
                    'link-url': link
                }
            );
        }
    }

    /**
     * adds product notifications
     */
    private async _addNotification() {
        await fetch(`/notification-settings/api/product-alerts/create/${this.partNumber}?locale=${this.locale}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(async (response) => {
            if (response.ok) {
                FeatureDialogService.closeDialog();
                const snackbarService = (window as any)?.com?.TI.SnackbarService ?? null;
                snackbarService?.showImmediately(this._localeStrings[this.locale]['snackbar'].replace('{0}', this.partNumber), this._localeStrings[this.locale]['view'], '/notification-settings/product-notifications');
                this._handleMetrics(`add ${this.type} notification`, this.partNumber, '/notification-settings/product-notifications');
            }
        }).catch((error) => {
            console.error(error);
        });
    }

    /**
     * open this dialog if the user is able to add this product to their notifications
     */
    private _openAddDialog(remaining: number, maximum: number) {
        this._dialogTitle = this._localeStrings[this.locale]['dialogTitle'].replace('{0}', this.partNumber);
        const dialogContent = ([
            <h3 slot="title">{this._dialogTitle}</h3>,
            <div class="ti-product-notifications-dialog-content">
                {(remaining <= 10) ?
                    <span class="ti_p-iconText u-margin-bottom-6" role="alert">
                        <span class="ti_p-iconText-icon">
                            <ti-svg-icon appearance="warn" icon-set="objects" size="s">warning</ti-svg-icon>
                        </span>
                        <span>{this._localeStrings[this.locale]['notificationLimit'].replace('{0}', `${remaining}`).replace('{1}', `${maximum}`)}</span>
                    </span> : ``}
                <p>{this._localeStrings[this.locale]['addNotificationBody']}</p>
            </div>,
                <ti-button appearance="primary" slot="action"
                    onClick={() => this._addNotification()}>
                    {this._localeStrings[this.locale]['add']}
                </ti-button>,
                <ti-button appearance="secondary" slot="action"
                    onClick={() => this._closeDialog()}>
                    {this._localeStrings[this.locale]['cancel']}
                </ti-button>
        ]);

        this._openDialog(dialogContent);
    }

    /**
     * open limit dialog if user already has this product added to their notifications
     */
    private _openDupeDialog() {
        this._dialogTitle = this._localeStrings[this.locale]['dialogTitle'].replace('{0}', this.partNumber);
        const dialogContent = ([
            <h3 slot="title">{this._dialogTitle}</h3>,
            <div class="ti-product-notifications-dialog-content">
                {this._localeStrings[this.locale]['alreadySubscribedBody']}
            </div>,
            <ti-button appearance="primary" slot="action">
                <a href="/notification-settings/product-notifications" onClick={() => { this._handleMetrics(`add ${this.type} notification`, 'view notifications', '/notification-settings/product-notifications');}}>
                    {this._localeStrings[this.locale]['view']}
                </a>
            </ti-button>,
            <ti-button appearance="secondary" slot="action"
                onClick={() => this._closeDialog()}>
                {this._localeStrings[this.locale]['cancel']}
            </ti-button>
        ]);

        this._openDialog(dialogContent);
    }

    /**
     * open limit dialog if user has no more space for additional notifications
     */
    private _openLimitDialog(limit) {
        this._dialogTitle = this._localeStrings[this.locale]['limitReached'];
        const dialogContent = ([
            <h3 slot="title">{this._dialogTitle}</h3>,
            <div innerHTML={this._localeStrings[this.locale]['limitReachedBody'].replace('{0}', `${limit}`)} class="ti-product-notifications-dialog-content"></div>,
            <ti-button appearance="primary" slot="action"
                    onClick={() => this._closeDialog()}>
                    {this._localeStrings[this.locale]['ok']}
                </ti-button>,
                <ti-button appearance="secondary" slot="action">
                    <a href="/notification-settings/product-notifications" onClick={() => { this._handleMetrics(`add ${this.type} notification`, 'view notifications', '/notification-settings/product-notifications');}}>
                        {this._localeStrings[this.locale]['manage']}
                    </a>
                </ti-button>
        ]);

        this._openDialog(dialogContent);
    }

    /**
     * open login dialog if user is not logged-in
     */
    private _openLoginDialog() {
        const sanitizedCurrentLocation = encodeURIComponent(window.location.href.replace('?login-check=true', ''));
        this._dialogTitle = this._localeStrings[this.locale]['dialogTitle'].replace('{0}', this.partNumber);
        const loginUrl = `/secure-link-forward/?gotoUrl=${sanitizedCurrentLocation}`;
        const dialogContent = ([
            <h3 slot="title">{this._dialogTitle}</h3>,
            <div class="ti-product-notifications-dialog-content">
                {this._localeStrings[this.locale]['loggedOutBody']}
            </div>,
                <ti-button appearance="primary" slot="action">
                    <a href={loginUrl} onClick={() => { this._handleMetrics(`add ${this.type} notification`, 'login', loginUrl)}}>
                        {this._localeStrings[this.locale]['login']}
                    </a>
                </ti-button>,
                <ti-button
                    appearance="secondary"
                    slot="action"
                    onClick={() => this._closeDialog()}>
                    {this._localeStrings[this.locale]['cancel']}
                </ti-button>
        ]);

        this._openDialog(dialogContent);
    }

    /**
     * open dialog using launcher service
     * @param dialogContent
     */
    private _openDialog(dialogContent) {
        if (this._isDialogOpen) {
            FeatureDialogService.updateDialogContent(dialogContent);
        } else {
            const dialogConfig = {
                class: '',
                content: dialogContent,
                modal: true
            };

            this._isDialogOpen = true;
            FeatureDialogService.openDialog(dialogConfig);
        }
    }

    /**
     * open progress dialog
     */
    private _openProgressDialog() {
        const dialogContent = ([
            <h3 slot="title">{this._dialogTitle}</h3>,
            <div><ti-progress></ti-progress></div>,
            <div slot="action"></div>
        ]);

        this._openDialog(dialogContent);
    }

    /**
     * init show dialog
     */
    private _showDialog() {

        // Show progress spinner in dialog while fetching
        this._openProgressDialog();

        fetchWithTimeout(this._externalProductEntryPointAPIUrl, {
            method: 'GET',
            credentials: 'include'
        }, this._timeout).then((response: any) => {
            if (response.ok) {
                return response.json();
            } else {
                return Promise.reject(new Error(`Failed to retrieve product alert status`));
            }
        }).then((json: {
            validAndEligiblePart: boolean;
            subscriptionLimit: number;
            subscriptionCount: number;
            isUserSubscribed: boolean;
            partNumber: string;
            isUserLoggedIn: boolean;
        }) => {

            if (!json.isUserLoggedIn) {
                this._openLoginDialog();
            } else if (json.isUserSubscribed) {
                this._openDupeDialog();
            } else if (json.subscriptionCount >= json.subscriptionLimit) {
                this._openLimitDialog(json.subscriptionLimit);
            } else {
                this._openAddDialog(json.subscriptionLimit - json.subscriptionCount, json.subscriptionLimit);
            }
        }).catch((err) => {
            console.error(err);
        });
        this._handleMetrics(`open ${this.type} notifications dialog`, `${this.type} notification link`, '');
    }


    /**
     * render the component
     */
    render() {
        return (
            <Host hidden={this.hide}>
                <ti-button appearance="link"
                    onClick={() => this._showDialog()}>
                    <ti-svg-icon appearance="tertiary" icon-set="actions" size="s">alarm</ti-svg-icon>
                    {this._localeStrings[this.locale]['linkName']}
                </ti-button>
            </Host>
        );
    }
}
